import { useEffect, useMemo, useState } from "react";
import { ArrowUpDown, ArrowUp, ArrowDown, Settings2, Trash2, Pencil, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/**
 * Reusable list table with sortable columns, per-user column visibility (stored
 * in localStorage), and multi-select + bulk delete (admin only).
 *
 * Props:
 *   storageKey   - unique key like "patients-cols"
 *   rows         - array of records (each must have a stable `id`)
 *   columns      - [{ key, label, accessor?, render?, align?, sortable?, defaultHidden?, className? }]
 *   isAdmin      - boolean
 *   onEdit       - (row) => void  (renders an Edit icon; admin-only)
 *   onDeleteMany - (ids[]) => Promise (renders bulk delete bar; admin-only)
 *   rowKey       - (row) => string. defaults to row.id
 *   emptyMessage - string
 *   extraRowActions - (row) => ReactNode  (rendered before edit/delete in the actions cell)
 */
export default function DataTable({
  storageKey,
  rows,
  columns,
  isAdmin = false,
  onEdit,
  onDeleteMany,
  rowKey = (r) => r.id,
  emptyMessage = "No records yet.",
  extraRowActions,
  minWidth = 640,
}) {
  const allKeys = useMemo(() => columns.map((c) => c.key), [columns]);

  const [visible, setVisible] = useState(() => loadVisible(storageKey, columns));
  const [sort, setSort] = useState(() => loadSort(storageKey)); // { key, dir: 'asc'|'desc' } | null
  const [selected, setSelected] = useState(() => new Set());

  useEffect(() => { saveVisible(storageKey, visible); }, [visible, storageKey]);
  useEffect(() => { saveSort(storageKey, sort); }, [sort, storageKey]);

  const toggleSort = (col) => {
    if (!col.sortable) return;
    if (!sort || sort.key !== col.key) { setSort({ key: col.key, dir: "asc" }); return; }
    if (sort.dir === "asc") { setSort({ key: col.key, dir: "desc" }); return; }
    setSort(null);
  };

  const sortedRows = useMemo(() => {
    if (!sort) return rows;
    const col = columns.find((c) => c.key === sort.key);
    if (!col) return rows;
    const acc = col.accessor || ((r) => r[col.key]);
    const copy = [...rows];
    copy.sort((a, b) => {
      const av = acc(a); const bv = acc(b);
      const result = compare(av, bv);
      return sort.dir === "asc" ? result : -result;
    });
    return copy;
  }, [rows, sort, columns]);

  const activeColumns = columns.filter((c) => visible[c.key] !== false);

  // selection helpers
  const allSelected = sortedRows.length > 0 && sortedRows.every((r) => selected.has(rowKey(r)));
  const someSelected = !allSelected && sortedRows.some((r) => selected.has(rowKey(r)));
  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(sortedRows.map(rowKey)));
  };
  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const clearSelection = () => setSelected(new Set());

  const showCheckboxes = isAdmin && !!onDeleteMany;
  const showActionsColumn = isAdmin && (onEdit || onDeleteMany);

  return (
    <div className="space-y-3" data-testid={`datatable-${storageKey}`}>
      {/* Toolbar: bulk-bar OR column config */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        {selected.size > 0 ? (
          <div className="flex items-center gap-3 text-sm" data-testid={`${storageKey}-bulk-bar`}>
            <span className="font-medium text-[color:var(--ink)]">{selected.size} selected</span>
            <button onClick={clearSelection} className="text-xs text-[color:var(--muted)] hover:text-[color:var(--ink)] underline-offset-2 hover:underline" data-testid={`${storageKey}-bulk-clear`}>Clear</button>
            <Button
              variant="outline"
              size="sm"
              className="rounded-full text-xs h-8 border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
              onClick={async () => {
                if (!window.confirm(`Delete ${selected.size} item(s)? This cannot be undone.`)) return;
                await onDeleteMany([...selected]);
                clearSelection();
              }}
              data-testid={`${storageKey}-bulk-delete`}
            >
              <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete selected
            </Button>
          </div>
        ) : <span />}

        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="rounded-full text-xs h-8" data-testid={`${storageKey}-columns-btn`}>
              <Settings2 className="w-3.5 h-3.5 mr-1.5" /> Columns
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-56 p-2" data-testid={`${storageKey}-columns-menu`}>
            <p className="text-[10px] font-semibold tracking-[0.18em] uppercase text-[color:var(--muted)] px-2 pt-1 pb-2">Visible columns</p>
            <div className="space-y-1">
              {columns.map((c) => (
                <label key={c.key} className="flex items-center gap-2 text-sm px-2 py-1.5 rounded-md hover:bg-[color:var(--bg-marketing)] cursor-pointer" data-testid={`${storageKey}-col-${c.key}`}>
                  <Checkbox
                    checked={visible[c.key] !== false}
                    onCheckedChange={(v) => setVisible((prev) => ({ ...prev, [c.key]: !!v }))}
                  />
                  <span className="text-[color:var(--ink)]">{c.label}</span>
                </label>
              ))}
            </div>
            <button
              onClick={() => setVisible(defaultVisibleMap(columns))}
              className="mt-2 w-full text-[11px] text-[color:var(--muted)] hover:text-[color:var(--ink)] underline-offset-2 hover:underline"
              data-testid={`${storageKey}-cols-reset`}
            >
              Reset to defaults
            </button>
          </PopoverContent>
        </Popover>
      </div>

      <div className="bg-white border border-[color:var(--border)] rounded-2xl overflow-hidden">
        {sortedRows.length === 0 ? (
          <p className="text-sm text-[color:var(--muted)] text-center py-14">{emptyMessage}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" style={{ minWidth: `${minWidth}px` }}>
              <thead className="bg-[color:var(--bg-marketing)]">
                <tr className="text-left text-xs font-semibold tracking-wider uppercase text-[color:var(--muted)]">
                  {showCheckboxes && (
                    <th className="px-4 py-3 w-9">
                      <Checkbox
                        checked={allSelected ? true : someSelected ? "indeterminate" : false}
                        onCheckedChange={toggleAll}
                        aria-label="Select all"
                        data-testid={`${storageKey}-select-all`}
                      />
                    </th>
                  )}
                  {activeColumns.map((c) => (
                    <th
                      key={c.key}
                      className={`px-5 py-3 select-none ${c.align === "right" ? "text-right" : ""} ${c.sortable ? "cursor-pointer hover:text-[color:var(--ink)]" : ""}`}
                      onClick={() => toggleSort(c)}
                      data-testid={`${storageKey}-th-${c.key}`}
                    >
                      <span className={`inline-flex items-center gap-1.5 ${c.align === "right" ? "justify-end w-full" : ""}`}>
                        {c.label}
                        {c.sortable && (
                          sort?.key === c.key
                            ? (sort.dir === "asc"
                              ? <ArrowUp className="w-3 h-3 text-[color:var(--primary)]" />
                              : <ArrowDown className="w-3 h-3 text-[color:var(--primary)]" />)
                            : <ArrowUpDown className="w-3 h-3 opacity-40" />
                        )}
                      </span>
                    </th>
                  ))}
                  {showActionsColumn && <th className="px-5 py-3 text-right">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-[color:var(--border)]">
                {sortedRows.map((r) => {
                  const id = rowKey(r);
                  const isSel = selected.has(id);
                  return (
                    <tr key={id} className={`hover:bg-[color:var(--bg-marketing)] ${isSel ? "bg-[color:var(--primary-light)]/15" : ""}`} data-testid={`${storageKey}-row-${id}`}>
                      {showCheckboxes && (
                        <td className="px-4 py-4 w-9">
                          <Checkbox checked={isSel} onCheckedChange={() => toggleOne(id)} aria-label="Select row" data-testid={`${storageKey}-select-${id}`} />
                        </td>
                      )}
                      {activeColumns.map((c) => (
                        <td key={c.key} className={`px-5 py-4 align-middle ${c.align === "right" ? "text-right" : ""} ${c.className || ""}`} data-testid={`${storageKey}-cell-${c.key}-${id}`}>
                          {c.render ? c.render(r) : (c.accessor ? c.accessor(r) : r[c.key]) ?? <span className="text-[color:var(--muted)]">—</span>}
                        </td>
                      ))}
                      {showActionsColumn && (
                        <td className="px-5 py-4 text-right">
                          <div className="inline-flex items-center gap-2 justify-end">
                            {extraRowActions && extraRowActions(r)}
                            {onEdit && (
                              <button
                                onClick={() => onEdit(r)}
                                className="text-[color:var(--muted)] hover:text-[color:var(--primary)] p-1"
                                aria-label="Edit"
                                data-testid={`${storageKey}-edit-${id}`}
                                title="Edit"
                              >
                                <Pencil className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/* ----- helpers ----- */
function compare(a, b) {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  // Date-like ISO strings sort correctly as strings, but coerce numerics safely.
  const an = Number(a); const bn = Number(b);
  if (!Number.isNaN(an) && !Number.isNaN(bn) && typeof a !== "string") return an - bn;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
}

function defaultVisibleMap(columns) {
  const m = {};
  for (const c of columns) m[c.key] = !c.defaultHidden;
  return m;
}

function loadVisible(storageKey, columns) {
  try {
    const raw = localStorage.getItem(`dt:visible:${storageKey}`);
    if (raw) {
      const parsed = JSON.parse(raw);
      // Make sure we still include any columns added after the user saved a config
      const merged = defaultVisibleMap(columns);
      for (const k of Object.keys(parsed)) {
        if (k in merged) merged[k] = !!parsed[k];
      }
      return merged;
    }
  } catch { /* ignore */ }
  return defaultVisibleMap(columns);
}

function saveVisible(storageKey, visible) {
  try { localStorage.setItem(`dt:visible:${storageKey}`, JSON.stringify(visible)); } catch { /* ignore */ }
}

function loadSort(storageKey) {
  try {
    const raw = localStorage.getItem(`dt:sort:${storageKey}`);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return null;
}

function saveSort(storageKey, sort) {
  try {
    if (sort) localStorage.setItem(`dt:sort:${storageKey}`, JSON.stringify(sort));
    else localStorage.removeItem(`dt:sort:${storageKey}`);
  } catch { /* ignore */ }
}

// Re-export for consumers who need the icon, e.g. badge cells.
export { Check };
